<script setup>
import { computed } from 'vue';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import MessageFormatter from 'shared/helpers/MessageFormatter.js';
import Avatar from 'next/avatar/Avatar.vue';
import { MESSAGE_TYPES } from './constants';

const props = defineProps({
  message: { type: Object, required: true },
});

const emit = defineEmits(['click']);

const ca = computed(() => props.message.content_attributes || {});

const isOutgoing = computed(
  () => props.message.message_type === MESSAGE_TYPES.OUTGOING
);

const authorName = computed(() => {
  if (ca.value.from?.name) return ca.value.from.name;
  if (props.message.sender?.name) return props.message.sender.name;
  return isOutgoing.value ? 'Agent' : 'User';
});

const avatarSrc = computed(() => props.message.sender?.thumbnail || '');

const readableTime = computed(() => {
  const ts = props.message.created_at;
  if (!ts) return '';
  return messageTimestamp(ts, 'LLL d, h:mm a');
});

const formattedContent = computed(() => {
  if (!props.message.content) return '';
  return new MessageFormatter(props.message.content).formattedMessage;
});

const attachments = computed(() => props.message.attachments || []);

const imageAttachment = computed(() => {
  const att = attachments.value[0];
  if (!att) return null;
  const fileType = att.file_type || att.fileType;
  if (fileType === 'image') return att;
  return null;
});

const bubbleClass = computed(() => {
  if (isOutgoing.value) {
    return 'bg-n-solid-blue text-n-slate-12 rounded-xl ltr:rounded-br-sm rtl:rounded-bl-sm';
  }
  return 'bg-n-slate-4 text-n-slate-12 rounded-xl ltr:rounded-bl-sm rtl:rounded-br-sm';
});
</script>

<template>
  <div
    class="flex gap-2 items-end rounded-lg p-1 transition-colors"
    :class="{
      'flex-row-reverse': isOutgoing,
    }"
  >
    <Avatar :name="authorName" :src="avatarSrc" :size="20" />
    <div class="flex flex-col min-w-0 max-w-md">
      <div
        class="px-3 py-2 text-sm cursor-pointer hover:brightness-90 transition-all"
        :class="bubbleClass"
        @click="emit('click', message.id)"
      >
        <span
          v-if="formattedContent"
          v-dompurify-html="formattedContent"
          class="prose prose-bubble break-words"
        />
        <img
          v-if="imageAttachment"
          :src="imageAttachment.data_url || imageAttachment.dataUrl"
          class="rounded-lg max-w-full max-h-40"
          :class="{ 'mt-1': formattedContent }"
        />
        <div
          class="flex items-center gap-1.5 mt-1 text-xs text-n-slate-11"
          :class="isOutgoing ? 'justify-end' : 'justify-start'"
        >
          <time>{{ readableTime }}</time>
        </div>
      </div>
    </div>
  </div>
</template>
